import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./liedBearbeiten.css";

const API = "https://liedersammlung.com/edit_add_api.php";

const blockTypes = [
  { value: "STROPHE", label: "Strophe" },
  { value: "CHOR", label: "Chor" }
];

const categories = [
  "",
  "auferstehung",
  "evangelisation",
  "gebet",
  "geburtstag",
  "gnade",
  "golgatha",
  "himmlische_heimat",
  "hingabe_und_dienst",
  "kampf_und_sieg",
  "kinderlieder",
  "lob_und_anbetung",
  "trost_und_vertrauen",
  "wiederkunft_christi",
  "wort_gottes"
];

function formatCategoryLabel(value) {
  if (!value) return "";
  const words = value.replace(/_/g, " ").split(" ").filter(Boolean);
  const lowerWords = new Set(["und", "oder", "von", "vom", "im", "am", "zu", "zur", "der", "die", "das"]);
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && lowerWords.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
}

function LiedHinzufuegen() {
  const navigate = useNavigate();
  const redirectTimer = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [form, setForm] = useState({
    name: "",
    tonlage: "",
    kategorie: "",
    bloecke: [{ block_typ: "STROPHE", text: "" }]
  });

  useEffect(() => {
    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, []);

  function updateBlock(index, field, value) {
    const copy = [...form.bloecke];
    copy[index] = { ...copy[index], [field]: value };
    setForm({ ...form, bloecke: copy });
  }

  function addBlock() {
    setForm({
      ...form,
      bloecke: [...form.bloecke, { block_typ: "STROPHE", text: "" }]
    });
  }

  function confirmRemoveBlock() {
    if (pendingRemove === null) {
      return;
    }

    const copy = [...form.bloecke];
    copy.splice(pendingRemove, 1);
    setForm({ ...form, bloecke: copy });
    setPendingRemove(null);
  }

  function goHome() {
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
    }

    navigate("/");
  }

  async function saveSong() {
    if (!form.name.trim() || !form.kategorie) {
      return;
    }

    setIsSaving(true);

    try {
      const response = await fetch(API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          action: "suggest_new_song",
          name: form.name,
          tonlage: form.tonlage,
          kategorie: form.kategorie,
          bloecke: form.bloecke.map((b) => ({
            block_typ: b.block_typ,
            text: b.text
          }))
        })
      });

      if (!response.ok) {
        throw new Error("Fehler beim Speichern");
      }

      setIsSaved(true);
      redirectTimer.current = setTimeout(() => navigate("/"), 7000);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div id="lied_bearbeiten_body">
      {!isSaved && (
        <>
          <section className="edit_header">
            <div>
              <h2>Lied hinzufügen</h2>
              <p className="edit_eyebrow">Verwaltung</p>
            </div>
          </section>

          <section className="song_editor">
            <div className="form_grid">
              <label>
                Titel
                <input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </label>

              <label>
                Tonlage
                <input
                  value={form.tonlage}
                  onChange={e => setForm({ ...form, tonlage: e.target.value })}
                />
              </label>

              <label className="wide_field">
                Kategorie
                <select
                  value={form.kategorie}
                  onChange={e => setForm({ ...form, kategorie: e.target.value })}
                >
                  {categories.map(category => (
                    <option key={category || "none"} value={category}>
                      {formatCategoryLabel(category)}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="blocks_head">
              <h3>Blöcke</h3>
              <span>{form.bloecke.length} Einträge</span>
            </div>

            <div className="blocks_list">
              {form.bloecke.map((block, index) => (
                <article className="edit_block" key={index}>
                  <div className="block_topline">
                    <span className="block_number">{String(index + 1).padStart(2, "0")}</span>
                    <select
                      aria-label="Blocktyp"
                      value={block.block_typ}
                      onChange={e => updateBlock(index, "block_typ", e.target.value)}
                    >
                      {blockTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <textarea
                    aria-label={`Text Block ${index + 1}`}
                    value={block.text}
                    onChange={e => updateBlock(index, "text", e.target.value)}
                  />

                  <div className="block_actions">
                    <span>{(block.text || "").length} Zeichen</span>
                    <button
                      className="text_btn danger"
                      type="button"
                      onClick={() => setPendingRemove(index)}
                      disabled={form.bloecke.length <= 1}
                    >
                      Entfernen
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <button className="add_block_btn" type="button" onClick={addBlock}>
              + Block
            </button>

            <div className="editor_actions">
              <button
                className="primary_btn"
                type="button"
                onClick={saveSong}
                disabled={!form.name.trim() || !form.kategorie || isSaving}
              >
                {isSaving ? "Speichert..." : "Speichern"}
              </button>
            </div>
          </section>
        </>
      )}

      {pendingRemove !== null && (
        <div className="confirm_overlay" role="dialog" aria-modal="true" aria-labelledby="remove_block_title">
          <div className="confirm_dialog">
            <h3 id="remove_block_title">Block entfernen?</h3>
            <p>Möchtest du diesen Block wirklich entfernen?</p>
            <div className="confirm_actions">
              <button className="secondary_btn" type="button" onClick={() => setPendingRemove(null)}>
                Abbrechen
              </button>
              <button className="primary_btn danger_btn" type="button" onClick={confirmRemoveBlock}>
                Entfernen
              </button>
            </div>
          </div>
        </div>
      )}

      {isSaved && (
        <div className="success_overlay" role="status" aria-live="polite">
          <div className="success_message">
            <h3>Danke für deine Unterstützung</h3>
            <p>Das Lied wird geprüft und dann freigegeben.</p>
            <button className="success_home_btn" type="button" onClick={goHome} aria-label="Zur Startseite">
              home
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiedHinzufuegen;
